import { useEffect, useState } from 'react';
import type { StepProps } from '../engine/context.ts';
import type { ProfileFact } from '../engine/types.ts';
import type { AnswerMap } from '../engine/answers.ts';
import { TitleBlock } from '../ui/TitleBlock.tsx';
import { asset } from '../engine/assets.ts';
import { CREAM, INK, LINE, MUTED, PAPER, SAND, SHADOW_CARD, VIOLET, YELLOW, ORANGE } from '../ui/theme.ts';

type Level = 0 | 1 | 2;

/**
 * profile: the manners level (from the cues score), a level card and a facts
 * card. Every string comes from the content block. Owner's rule: nobody
 * arrives "Advanced"; a perfect quiz still lands on Intermediate (pct ≥ 30),
 * so the plan always has somewhere to take the dog. Answer: { value, label }.
 */
export function levelFor(answers: AnswerMap): Level {
  const score = answers.score as { value?: number } | undefined;
  const cues = answers.cues as { count?: number; values?: unknown[] } | undefined;
  const pct = score?.value ?? Math.round(((cues?.count ?? cues?.values?.length ?? 0) / 4) * 100);
  if (pct >= 30) return 1;
  return 0;
}

const labelsOf = (a: unknown): string[] => {
  if (!a || typeof a !== 'object') return [];
  const o = a as { labels?: string[]; label?: string; value?: unknown };
  if (Array.isArray(o.labels)) return o.labels;
  if (typeof o.label === 'string' && o.label) return [o.label];
  if (typeof o.value === 'string' && o.value) return [o.value];
  return [];
};

export function factValue(fact: ProfileFact, answers: AnswerMap): string {
  if (fact.kind === 'behaviour') {
    const aggr = (answers.aggr as { index?: number } | undefined)?.index ?? 0;
    const treat = (answers.treat as { index?: number } | undefined)?.index ?? 1;
    if (aggr > 0) return fact.reactive ?? '';
    if (treat < 2) return fact.distracted ?? '';
    return fact.steady ?? '';
  }
  const keys = (fact.from ?? '').split(',').map((k) => k.trim()).filter(Boolean);
  if (fact.present !== undefined || fact.absent !== undefined) {
    return keys.some((k) => labelsOf(answers[k]).length) ? (fact.present ?? '') : (fact.absent ?? '');
  }
  for (const k of keys) {
    const labels = labelsOf(answers[k]);
    if (labels.length) return labels.slice(0, 2).join(', ');
  }
  return fact.fallback ?? '';
}

export function Profile({ step, content, ctx }: StepProps) {
  const level = levelFor(ctx.answers);
  const levels = content.levels ?? ['Basic', 'Intermediate', 'Advanced'];
  const bodies = [content.levelBody?.basic, content.levelBody?.intermediate, content.levelBody?.advanced];
  const [knob, setKnob] = useState(0);
  const slot = step.asset ?? content.asset;

  useEffect(() => {
    ctx.setAnswer('level', { value: ['basic', 'intermediate', 'advanced'][level], label: levels[level], index: level });
    ctx.setCta({ label: content.cta ?? ctx.shared.ui?.continue ?? 'Continue', large: true, onPress: () => ctx.next() });
    const t = setTimeout(() => setKnob(level), 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.id, level]);

  const pos = [8, 50, 92][knob];

  return (
    <>
      <TitleBlock label={content.label} title={content.title} highlight={content.highlight} subtitle={content.subtitle} />

      {/* level slider */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {content.levelLabel ? <div style={{ fontWeight: 800, fontSize: 15 }}>{content.levelLabel}</div> : null}
        <div style={{ position: 'relative', height: 14, borderRadius: 7, background: `linear-gradient(90deg, ${VIOLET}, ${YELLOW}, ${ORANGE})` }} aria-hidden="true">
          <span
            style={{
              position: 'absolute',
              top: '50%',
              left: `${pos}%`,
              width: 26,
              height: 26,
              borderRadius: '50%',
              background: PAPER,
              border: `3px solid ${INK}`,
              transform: 'translate(-50%, -50%)',
              transition: 'left .6s cubic-bezier(.2,.8,.2,1)',
              boxShadow: SHADOW_CARD,
            }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 800, color: MUTED }}>
          {levels.map((l, i) => (
            <span key={l} style={{ color: i === level ? INK : MUTED }}>
              {l}
            </span>
          ))}
        </div>
      </div>

      {/* level card */}
      <div style={{ padding: '14px 16px', borderRadius: 18, background: PAPER, border: `2px solid ${VIOLET}`, boxShadow: SHADOW_CARD }} aria-live="polite">
        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: MUTED }}>{levels[level]}</div>
        {bodies[level] ? <div style={{ fontSize: 14.5, fontWeight: 600, marginTop: 4, color: INK }}>{bodies[level]}</div> : null}
      </div>

      {/* facts card */}
      {content.facts?.length ? (
        <div style={{ padding: 14, borderRadius: 18, background: SAND, border: `1.5px solid ${LINE}`, display: 'grid', gridTemplateColumns: slot ? '1fr 96px' : '1fr', gap: 12, alignItems: 'start' }}>
          <div style={{ display: 'grid', gap: 10 }}>
            {content.facts.map((f) => (
              <div key={f.label} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                {f.emoji ? (
                  <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1.3 }}>
                    {f.emoji}
                  </span>
                ) : null}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: MUTED }}>{f.label}</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: INK }}>{factValue(f, ctx.answers)}</div>
                </div>
              </div>
            ))}
          </div>
          {slot ? (
            <div style={{ width: 96, aspectRatio: '11 / 13', borderRadius: 14, overflow: 'hidden', background: CREAM }}>
              <img src={asset(slot)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" />
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
